import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MenuNotFound from "@/components/MenuNotFound";
import LoadingWaves from "@/components/LoadingWaves";
import BrandFooter from "@/components/BrandFooter";
import BrandIcon from "@/components/BrandIcon";
import { useTenantHome } from "@/hooks/useBranchMenu";
import { setFavicon } from "@/helpers/favicon";
import {
  buildLinkHref,
  isMapsHref,
  opensInSameTab,
  whatsappFromTel,
} from "@/helpers/branchLinks";
// Şubede kendi görseli (branch.imageUrl, panelden yüklenir) yoksa bu fallback kullanılır.
import { DEFAULT_BRANCH_IMAGE } from "@/helpers/storage";

// Kartta gösterilen eylemler: şubeye ŞİMDİ ulaşmanın yolları — ara, yaz, git. Instagram/web gibi
// marka bağlantıları menü sayfasında duruyor (place-info__contact); burası şube SEÇME ekranı, o
// yüzden liste bilerek kısa. Sıra da buradan geliyor, paneldeki link sırasından değil: kartlar
// arasında aynı eylem hep aynı yerde olsun — sütunda en üstte harita, altında iletişim.
//
// Eşleşme ANAHTARA değil çözülmüş ADRESE bakıyor. Panelde tür listesi kalkıp yerine simge seçici
// geldi; "hangi link telefon" sorusunun anahtardan cevabı kalmadı (📞 de ☎️ de olabilir, ya da
// kullanıcı bambaşka bir şey seçebilir). Adres ise artık her zaman tam yazılıyor, yani şema ve alan
// adı güvenilir tek kaynak.
//
// Simge bağlantının KENDİsinden geliyor: kullanıcı panelde telefon satırına hangi emojiyi seçtiyse
// kartta da o görünüyor. Eskiden burası sabit marka simgesi basıyordu ve aynı şube aynı telefonu
// menüde 📞, kartta yeşil ahize olarak gösteriyordu — aynı veri iki ekranda iki türlü.
//
// `fallback` yalnızca KAYNAK BAĞLANTI YOKKEN devreye giriyor: WhatsApp çoğu şubede ayrı satır
// olarak girilmiyor, cep numarasından türetiliyor (aşağıya bak) ve türetilmiş satırın seçilmiş bir
// emojisi yok. Maps/telefon içinse pratikte hep bir link var, fallback ölü değil ama nadir.
//
// Üçü de ETİKETSİZ: simgeler zaten tanınıyor ve kart 320px genişlikte de sığmalı. `label` yalnızca
// aria-label'da yaşıyor.
const WHATSAPP_HREF = /\/\/(wa\.me|api\.whatsapp\.com)\b/i;

const CARD_ACTIONS = [
  {
    key: "maps",
    label: "Yol Tarifi",
    fallback: "brand:maps",
    match: isMapsHref,
  },
  {
    key: "phone",
    label: "Ara",
    fallback: "brand:phone",
    match: (href) => /^tel:/i.test(href),
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    fallback: "brand:whatsapp",
    match: (href) => WHATSAPP_HREF.test(href),
  },
];

// Panelde kapatılan bağlantı (active === false) listede kalır ama menüde gizlenir — eksik alan
// "açık" demek. DefaultMenu aynı kuralı uyguluyor; burada eksikti, kapalı harita linki yine de
// "Yol Tarifi" çipi basıyordu.
const cardActions = (links) => {
  const entries = (links ?? [])
    .filter((link) => link.active !== false)
    .map((link) => ({
      icon: link.icon,
      href: buildLinkHref(link.icon, link.value),
    }));

  // Açık bir wa.me bağlantısı yoksa cep numarasından türetiliyor: çoğu şubede ikisi aynı numara
  // ve ayrı satır girmek gereksiz iş. Sabit hatta türetilmiyor (bkz. whatsappFromTel), ve girilen
  // açık bağlantı her zaman önce geliyor — listenin SONUNA ekleniyor, `find` ilki bulur.
  //
  // Türetilen satırın simgesi YOK: kaynağı telefon bağlantısı ve onun emojisini kullanmak kartta
  // iki özdeş simge demek olurdu. `fallback` (brand:whatsapp) devreye giriyor.
  if (!entries.some((entry) => WHATSAPP_HREF.test(entry.href))) {
    const derived = entries
      .map((entry) => whatsappFromTel(entry.href))
      .find(Boolean);

    if (derived) {
      entries.push({ icon: null, href: derived });
    }
  }

  return CARD_ACTIONS.map((action) => {
    const entry = entries.find((item) => action.match(item.href));

    return entry
      ? { ...action, href: entry.href, icon: entry.icon || action.fallback }
      : null;
  }).filter(Boolean);
};

// Root page: single branch redirects straight to its menu, multiple branches show a picker.
export default function BranchPicker() {
  const navigate = useNavigate();
  const { data, fetching, failKind } = useTenantHome();

  const branches = data?.branches;
  const tenant = data?.tenant;
  // .bp-logo 96px kare bir kutu — geniş marka kilidi burada okunmaz, o yüzden varsa simge önce.
  const logoSrc =
    tenant?.markUrl ||
    tenant?.logoUrl ||
    tenant?.icons?.appleTouch ||
    tenant?.icons?.favicon;

  useEffect(() => {
    setFavicon({
      ...tenant?.icons,
      markUrl: tenant?.markUrl,
      logoUrl: tenant?.logoUrl,
    });
    if (tenant?.name) {
      document.title = tenant.name;
    }
  }, [tenant?.icons, tenant?.markUrl, tenant?.logoUrl, tenant?.name]);

  useEffect(() => {
    if (!fetching && branches?.length === 1) {
      navigate(`/${branches[0].slug}`, { replace: true });
    }
  }, [fetching, branches, navigate]);

  if (fetching || branches?.length === 1) {
    return <LoadingWaves />;
  }

  if (failKind && failKind !== "notFound") {
    return (
      <MenuNotFound title="Bağlantı Hatası" message="Lütfen tekrar deneyin." />
    );
  }

  if (failKind || !branches?.length) {
    return <MenuNotFound title="Menü Bulunamadı" />;
  }

  return (
    <div className="bp-root">
      <style>{`
                .bp-root {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 40px 20px 1px;
                    background-color: #f1e9df;
                    background-image: var(--brand-splash-bg);
                    font-family: var(--primary-font-set, system-ui, sans-serif);
                    color: #3a2c1e;
                }
                .bp-inner { width: 100%; max-width: 440px; }
                .bp-header { text-align: center; margin-bottom: 26px; }
                .bp-logo {
                    width: 96px; height: 96px; margin: 0 auto 14px; border-radius: 24px;
                    object-fit: contain; background: #fff; padding: 10px;
                    border: 1px solid rgba(139, 94, 52, 0.14);
                    box-shadow: 0 14px 34px rgba(90, 60, 30, 0.14);
                }
                .bp-logo-fallback {
                    width: 96px; height: 96px; margin: 0 auto 14px; border-radius: 24px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 40px; font-weight: 700; color: #b07a42;
                    background: #fff; border: 1px solid rgba(139, 94, 52, 0.14);
                    box-shadow: 0 14px 34px rgba(90, 60, 30, 0.14);
                }
                .bp-name { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
                .bp-list { display: flex; flex-direction: column; gap: 14px; }
                .bp-card {
                    position: relative; display: block; width: 100%; height: 128px;
                    border: 0; padding: 0; margin: 0; border-radius: 20px; overflow: hidden; cursor: pointer;
                    background: var(--bp-card-img) center / cover no-repeat, #e7ddd0;
                    box-shadow: 0 8px 22px rgba(90, 60, 30, 0.13);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    animation: bp-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
                    font: inherit; color: inherit; text-align: left;
                    /* Çiple aynı sebep: kart da tıklanabilir, dokununca arkasına gri dikdörtgen
                       biniyordu. Geri bildirimi aşağıdaki :active veriyor. */
                    -webkit-tap-highlight-color: transparent;
                }
                .bp-card:hover { transform: translateY(-3px); box-shadow: 0 18px 34px rgba(90, 60, 30, 0.22); }
                .bp-card:active { transform: translateY(-1px) scale(0.995); }
                .bp-card:focus-visible { outline: 2px solid #b07a42; outline-offset: 2px; }
                .bp-card-body {
                    position: absolute; left: 0; right: 0; bottom: 0;
                    padding: 14px 16px;
                    background: linear-gradient(to top, rgba(0, 0, 0, 0.62), rgba(0, 0, 0, 0));
                }
                /* Çip sütunu sağda duruyor; ad onun altına girmesin diye sağdan daralıyor.
                   Yalnızca çip VARSA: eylemi olmayan şubede ad kartın tam genişliğini kullanmalı. */
                .bp-card-body.has-actions { padding-right: 60px; }
                /* Gölgesiz: ad doğrudan fotoğrafın üstünde duruyor. Karartma katmanı (.bp-scrim) da
                   yok, yani okunurluğu tamamen görselin kendi kontrastı taşıyor — açık zeminli bir
                   şube fotoğrafında beyaz yazı sönük kalabilir, kadraj ona göre seçilmeli. */
                .bp-card-name {
                    display: block;
                    color: #fff; font-size: 19px; font-weight: 700; letter-spacing: 0.2px;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                /* Eylemler SAĞ kenarda, dikey sütun ve dikeyde ortalı.
                   Liste ÜÇ eylemle sınırlı (harita, telefon, WhatsApp) ve ölçü ona göre: 3 × 32px +
                   2 × 6px = 108px, kartın 128px'ine sığıyor. Sütunun taşmaması bu sayıya bağlı. */
                .bp-actions {
                    position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
                    display: flex; flex-direction: column; gap: 2px; z-index: 1;
                }
                /* Hepsi etiketsiz DAİRE, 32px — parmak hedefi olarak alt sınır. Harita çipi bir
                   ara "Yol Tarifi" yazısını taşıyordu; kaldırılınca etiketli varyant boşta kaldı. */
                .bp-chip {
                    display: inline-flex; align-items: center; justify-content: center;
                    position: relative;
                    width: 32px; height: 32px; border-radius: 999px;
                    background: rgba(255, 255, 255, 0.94); color: #2a2118;
                    text-decoration: none;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                    transition: transform 0.15s ease, background 0.15s ease;
                    /* Mobil tarayıcı dokunulan bağlantının ARDINA gri bir dikdörtgen basıyor.
                       Çip daire olduğu için bu kare köşeler halinde taşıyor ve dokunma anında
                       gölge gibi görünüyor. Kapatılıyor; geri bildirimi zaten :active veriyor. */
                    -webkit-tap-highlight-color: transparent;
                }
                .bp-chip::after {
                    content: ''; position: absolute; top: 50%; left: 50%;
                    width: 40px; height: 40px; transform: translate(-50%, -50%);
                }
                .bp-chip:hover { transform: scale(1.05); background: #fff; }
                /* Parmağın altında küçülüyor — kaldırılan vurgunun yerine geçen dokunma geri
                   bildirimi. :hover'DAN SONRA gelmeli: ikisi de (0,2,0) özgüllükte, masaüstünde
                   basılı tutarken ikisi birden eşleşiyor ve sonra yazılan kazanıyor. */
                .bp-chip:active { transform: scale(0.92); }
                .bp-chip svg { display: block; }
                .bp-footer { margin-top: auto; padding-top: 34px; text-align: center; }
                @keyframes bp-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

      <div className="bp-inner">
        <div className="bp-header">
          {logoSrc ? (
            <img className="bp-logo" src={logoSrc} alt={tenant.name} />
          ) : (
            <div className="bp-logo-fallback">{tenant?.name?.[0] ?? "🍽"}</div>
          )}
          <h1 className="bp-name">{tenant?.name}</h1>
        </div>

        <div className="bp-list">
          {branches.map((branch, index) => {
            const actions = cardActions(branch.links);
            const go = () => navigate(`/${branch.slug}`);
            return (
              <div
                className="bp-card"
                key={branch.slug}
                role="button"
                tabIndex={0}
                aria-label={branch.name}
                onClick={go}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    go();
                  }
                }}
                style={{
                  "--bp-card-img": `url('${branch.imageUrl || DEFAULT_BRANCH_IMAGE}')`,
                  animationDelay: `${index * 60}ms`,
                }}
              >
                <span
                  className={`bp-card-body${actions.length ? " has-actions" : ""}`}
                >
                  <span className="bp-card-name">{branch.name}</span>
                </span>
                {actions.length > 0 && (
                  <span className="bp-actions">
                    {actions.map((action) => (
                      <a
                        key={action.key}
                        className="bp-chip"
                        href={action.href}
                        // tel: aynı sekmede açılmalı — yeni sekme, çevirici
                        // kapanınca geride boş bir pencere bırakıyor.
                        // Karar adresten, DefaultMenu ile aynı kuraldan.
                        target={
                          opensInSameTab(action.href) ? undefined : "_blank"
                        }
                        rel="noreferrer"
                        aria-label={`${branch.name} — ${action.label}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <BrandIcon kind={action.icon} />
                      </a>
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <footer className="bp-footer">
        <BrandFooter />
      </footer>
    </div>
  );
}
